import React, { useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  useGetOrderCountByEmiratesQuery,
  useGetOrderCountByTypeQuery,
  useGetPeriodicOrderCountQuery,
} from "src/services/dashboard/dashboard";
import moment from "moment";
import { FadeLoader } from "react-spinners";
import InventoryMap from "src/components/Maps/inventoryMap";
import { useGetInventoryDailyDataQuery } from "src/services/inventory/inventory";
import { useTheme } from "src/contexts/ThemeContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface IDashboardProps { }

export function Dashboard(props: IDashboardProps) {
  const { theme } = useTheme();
  const [timeFrame, setTimeFrame] = useState<"daily" | "monthly" | "yearly">(
    "daily"
  );
  const [orderTimeFrame, setOrderTimeFrame] = useState<
    "daily" | "weekly" | "monthly"
  >("daily");
  const {
    data: periodicData,
    isLoading,
  } = useGetPeriodicOrderCountQuery(timeFrame);
  const { data: completedOrdersData } =
    useGetOrderCountByTypeQuery("completed");
  const { data: inTransitOrdersData } =
    useGetOrderCountByTypeQuery("in_transit");
  const { data: shipperCountData } = useGetOrderCountByTypeQuery("shipper");
  const { data: haulierCountData } = useGetOrderCountByTypeQuery("transporter");

  const {
    data: orderCountByEmiratesData,
    isLoading: isOrderCountByEmiratesLoading,
    error: orderCountByEmiratesError,
  } = useGetOrderCountByEmiratesQuery(orderTimeFrame);

  // const {
  //   data: dailyData,
  //   isLoading: isHubLoading,
  //   error: hubError,
  // } = useGetInventoryDailyDataQuery();

  // Prepare data based on the selected time frame
  const data = {
    labels: [] as string[],
    datasets: [
      {
        label: "Order Count",
        data: [] as number[],
        backgroundColor: "#7678ED",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 0,
        borderRadius: 8,
        barPercentage: 0.6,
        categoryPercentage: 0.6,
      },
    ],
  };

  function formatDate(
    dateString: string,
    timeFrame: "daily" | "monthly" | "yearly"
  ): string {
    const date = moment(dateString);
    if (timeFrame === "daily") {
      return date.format("DD MMM"); // e.g., '01 Nov'
    } else if (timeFrame === "monthly") {
      return date.format("MMM YYYY"); // e.g., 'Nov 2024'
    } else if (timeFrame === "yearly") {
      return date.format("YYYY"); // e.g., '2024'
    }
    return "";
  }

  // Update chart data when API data is available
  if (periodicData && !isLoading) {
    data.labels = periodicData.map((item: any) =>
      formatDate(item.start, timeFrame)
    );
    data.datasets[0].data = periodicData.map((item: any) => item.count);
  }

  // Read chart colors from CSS variables so they respond to theme changes
  const chartGridColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-grid').trim() || 'rgba(30,42,58,0.1)';
  const chartTickColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--chart-ticks').trim() || '#64748B';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTickColor,
        },
      },
      y: {
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTickColor,
          precision: 0,
        },
      },
    },
  };

  // Mapping of emirate names to codes
  const emirateCodes: { [key: string]: string } = {
    dubai: "DXB",
    "abu dhabi": "AUH",
    sharjah: "SHJ",
    ajman: "AJM",
    "umm al quwain": "UAQ",
    "ras al khaimah": "RAK",
    fujairah: "FUJ",
  };

  // Helper function to capitalize emirate names
  function capitalize(str: string) {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  // Map API data to display format
  const ordersToDisplay = React.useMemo(() => {
    if (orderCountByEmiratesData && !isOrderCountByEmiratesLoading) {
      return orderCountByEmiratesData
        .map((item: any) => {
          const originCode =
            emirateCodes[item.origin.toLowerCase()] || item.origin;
          const destinationCode =
            emirateCodes[item.destination.toLowerCase()] || item.destination;
          return {
            route: `${originCode.toUpperCase()} to ${destinationCode.toUpperCase()}`,
            desc: `${capitalize(item.origin)} to ${capitalize(
              item.destination
            )}`,
            count: item.count,
          };
        })
        .filter((item: any) => item.count > 0);
    }
    return [];
  }, [orderCountByEmiratesData, isOrderCountByEmiratesLoading]);

  return (
    <div className="w-full mt-2" style={{ backgroundColor: 'var(--bg-base)', transition: 'background-color 0.25s ease' }}>
      <div className="flex flex-col w-full h-screen p-6 pt-6 overflow-auto" style={{ backgroundColor: 'var(--bg-base)' }}>
        {/* First Row */}
        <div className="flex flex-wrap -mx-2">
          {/* Card 1: Completed orders */}
          <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
            <div className="flex flex-col rounded-lg p-4 hover:shadow-lg transition-shadow duration-200"
              style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
              <p className="text-md mb-2" style={{ color: 'var(--text-muted)' }}>Completed orders</p>
              <div className="flex items-center">
                <div>
                  <h1 className="text-3xl font-semibold" style={{ color: 'var(--text-main)' }}>
                    {completedOrdersData?.result?.thisMonthCount || 0}
                  </h1>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: In transit orders */}
          <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
            <div className="flex flex-col rounded-lg p-4 hover:shadow-lg transition-shadow duration-200"
              style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
              <p className="text-md mb-2" style={{ color: 'var(--text-muted)' }}>In transit orders</p>
              <div className="flex items-center">
                <div>
                  <h1 className="text-3xl font-semibold" style={{ color: 'var(--text-main)' }}>
                    {inTransitOrdersData?.result?.thisMonthCount || 0}
                  </h1>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Shipper count */}
          <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
            <div className="flex flex-col rounded-lg p-4 hover:shadow-lg transition-shadow duration-200"
              style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
              <p className="text-md mb-2" style={{ color: 'var(--text-muted)' }}>Shipper count</p>
              <div className="flex items-center">
                <div>
                  <h1 className="text-3xl font-semibold" style={{ color: 'var(--text-main)' }}>
                    {shipperCountData?.result?.thisMonthCount || 0}
                  </h1>
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Haulier count */}
          <div className="w-full sm:w-1/2 lg:w-1/4 px-2 mb-4">
            <div className="flex flex-col rounded-lg p-4 hover:shadow-lg transition-shadow duration-200"
              style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
              <p className="text-md mb-2" style={{ color: 'var(--text-muted)' }}>Haulier count</p>
              <div className="flex items-center">
                <div>
                  <h1 className="text-3xl font-semibold" style={{ color: 'var(--text-main)' }}>
                    {haulierCountData?.result?.thisMonthCount || 0}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Second Row */}
        <div className="flex flex-row -mx-2">
          {/* Order Activity Chart */}
          <div className="w-[70%] rounded-lg px-2" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
            <div className="flex items-center p-4 ">
              <h1 className="text-lg font-semibold" style={{ color: 'var(--text-main)' }}>Lock Count</h1>
            </div>
            {/* {isHubLoading ? (
              <div className="flex flex-col items-center justify-center text-white text-center h-[80%]">
                <FadeLoader color="#FF3B45" speedMultiplier={1} />
              </div>
            ) : hubError ? (
              <div className="flex flex-col items-center justify-center text-white text-center h-[90%]">
                <div className="bg-white/[0.16] p-2 rounded-2xl mb-6">
                  <img
                    src="assets/icons/no-data.svg"
                    alt="Order"
                    className="h-10 w-10"
                  />
                </div>
                <h5 className="mb-2 font-semibold">No data</h5>
                <p className="text-secondary">
                  Error loading map data.
                </p>
              </div>
            ) : (
              <InventoryMap data={dailyData} height="88%" />
            )} */}
          </div>

          {/* Order Count Section */}
          <div className="w-[30%] px-2">
            <div className="rounded-lg h-full flex flex-col" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
              <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
                <h1 className="text-lg font-semibold" style={{ color: 'var(--text-main)' }}>
                  Order count
                </h1>
                <select
                  className="rounded px-2 py-1 w-28 focus:ring-0"
                  style={{ backgroundColor: 'var(--bg-surface-2)', border: '1px solid var(--border-light)', color: 'var(--text-main)' }}
                  value={orderTimeFrame}
                  onChange={(e) =>
                    setOrderTimeFrame(
                      e.target.value as "daily" | "weekly" | "monthly"
                    )
                  }
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div
                className="p-4 h-full overflow-y-hidden"
                style={{ minHeight: "500px", maxHeight: "500px" }}
              >
                {isOrderCountByEmiratesLoading ? (
                  <div className="flex flex-col items-center justify-center text-center h-[80%]" style={{ color: 'var(--text-main)' }}>
                    <FadeLoader color="var(--color-primary)" speedMultiplier={1} />
                  </div>
                ) : ordersToDisplay.length > 0 ? (
                  ordersToDisplay.map(
                    (
                      order: { route: string; desc: string; count: number },
                      index: number
                    ) => (
                      <div
                        key={index}
                        className="flex justify-between items-center mb-4 pb-3"
                        style={{ borderBottom: '1px solid var(--border-light)' }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-overlay)' }}>
                            <img
                              src="assets/icons/order-cube.svg"
                              alt="Order"
                            />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm" style={{ color: 'var(--text-main)' }}>
                              {order.route}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {order.desc}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-main)' }}>
                          {order.count}
                        </span>
                      </div>
                    )
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center text-center h-[90%]" style={{ color: 'var(--text-main)' }}>
                    <div className="p-2 rounded-2xl mb-6" style={{ backgroundColor: 'var(--bg-overlay)' }}>
                      <img
                        src="assets/icons/no-data.svg"
                        alt="Order"
                        className="h-10 w-10"
                      />
                    </div>
                    <h5 className="mb-2 font-semibold">No data</h5>
                    <p style={{ color: 'var(--text-muted)' }}>
                      At the moment, there is no available data. <br /> Please
                      check back later for updates.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Order Activity Chart */}

        <div className="rounded-lg w-full px-2 mt-6 mb-20" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--text-main)' }}>Order activity</h1>
            <select
              className="rounded px-2 py-1 w-28 focus:ring-0"
              style={{ backgroundColor: 'var(--bg-surface-2)', border: '1px solid var(--border-light)', color: 'var(--text-main)' }}
              value={timeFrame}
              onChange={(e) =>
                setTimeFrame(e.target.value as "daily" | "monthly" | "yearly")
              }
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="p-4 h-[450px]">
            {isLoading ? (
              <div style={{ color: 'var(--text-muted)' }}>Loading...</div>
            ) : periodicData && periodicData.length > 0 ? (
              <Bar data={data} options={options} />
            ) : (
              <div className="flex flex-col items-center justify-center text-center h-full" style={{ color: 'var(--text-main)' }}>
                <div className="p-2 rounded-2xl mb-6" style={{ backgroundColor: 'var(--bg-overlay)' }}>
                  <img
                    src="assets/icons/no-data.svg"
                    alt="Order"
                    className="h-10 w-10"
                  />
                </div>
                <h5 className="mb-2 font-semibold">No data</h5>
                <p style={{ color: 'var(--text-muted)' }}>
                  At the moment, there is no available data. <br /> Please check
                  back later for updates.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-2"></div>
      </div>
    </div>
  );
}
